import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Activity, AlertCircle, Award, CheckCircle, Play, Loader2, FileText,
    BarChart2, History, Users, Sparkles, ArrowRight, RefreshCw,
    ClipboardCheck, Target, TrendingUp, ChevronRight, AlertTriangle, Info, BookOpen,
    Building2, GraduationCap, HeartHandshake, Briefcase, Gavel, Smile, ThumbsUp, Flag,
    ChevronLeft
} from 'lucide-react';
import SQAAFActionPlan from './sqaaf/SQAAFActionPlan';
import apiService from '../../services/api';
import SQAAFQuizWizard from './sqaaf/SQAAFQuizWizard';
import SQAAFScoreReport from './sqaaf/SQAAFScoreReport';
import SQAAFHistory from './sqaaf/SQAAFHistory';

const SQAAF = ({ noLayout = false, academicYear: yearProp = null }) => {
    const Wrap = ({ children }) => <>{children}</>;
    const navigate = useNavigate();
    const { year: urlYear } = useParams();

    // Resolve the active academic year: prop > SDP localStorage (when embedded) > URL param > null (API default)
    const resolvedYear = yearProp ||
        (noLayout ? (() => { try { return JSON.parse(localStorage.getItem('school_portal_settings') || '{}').academicYear || null; } catch { return null; } })() : null) ||
        urlYear ||
        null;
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState('dashboard');
    const [framework, setFramework] = useState(null);
    const [currentAudit, setCurrentAudit] = useState(null);
    const [notification, setNotification] = useState(null);
    const [teacherStats, setTeacherStats] = useState(null);
    const [showRestartConfirm, setShowRestartConfirm] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const user = JSON.parse(localStorage.getItem('user_info') || '{}');
            const schoolId = user.school_id || localStorage.getItem('school_id');

            if (!schoolId) {
                showNotification('error', 'School ID not found. Please relogin.');
                return;
            }

            const [fwPayload, auditPayload] = await Promise.all([
                apiService.sqaaf.getFrameworkOverview(),
                apiService.sqaaf.getCurrentAudit(schoolId, resolvedYear)
            ]);

            setFramework(fwPayload.domains);
            setCurrentAudit(auditPayload);

            try {
                const evidenceData = await apiService.sqaaf.getAutoEvidence();
                setTeacherStats(evidenceData.raw_stats);
            } catch (e) {
                console.log('Auto-evidence not available');
            }
        } catch (error) {
            console.error("Failed to load SQAAF data", error);
            showNotification('error', 'Failed to load data.');
        } finally {
            setLoading(false);
        }
    };

    const startAudit = async () => {
        try {
            setLoading(true);
            const user = JSON.parse(localStorage.getItem('user_info') || '{}');
            const schoolId = user.school_id || localStorage.getItem('school_id');
            const yearToUse = resolvedYear || new Date().getFullYear() + '-' + String(new Date().getFullYear() + 1).slice(-2);

            if (!currentAudit) {
                const newAudit = await apiService.sqaaf.startAudit(schoolId, yearToUse);
                setCurrentAudit(newAudit);
            }
            setViewMode('wizard');
        } catch (error) {
            showNotification('error', 'Could not start assessment.');
        } finally {
            setLoading(false);
        }
    };

    const restartAudit = async () => {
        try {
            setLoading(true);
            setShowRestartConfirm(false);
            const user = JSON.parse(localStorage.getItem('user_info') || '{}');
            const schoolId = user.school_id || localStorage.getItem('school_id');

            // Call restart endpoint (archives old, creates new)
            await apiService.sqaaf.restartAudit(schoolId);
            showNotification('success', 'Assessment restarted successfully!');
            await loadData();
            setViewMode('wizard');
        } catch (error) {
            console.error("Failed to restart audit", error);
            showNotification('error', 'Could not restart assessment.');
        } finally {
            setLoading(false);
        }
    };

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => setNotification(null), 4000);
    };

    // Calculate progress and scores
    const getStats = () => {
        if (!framework || framework.length === 0 || !currentAudit) {
            return { percent: 0, answered: 0, total: 0, score: 0, avgLevel: 0 };
        }

        let totalIndicators = 0;
        let totalWeightage = 0;
        let weightedScoreSum = 0;
        let totalLevelSum = 0;
        let answeredCount = 0;

        framework.forEach(domain => {
            const domainIndicators = (domain.subdomains || []).flatMap(s => s.indicators || []);
            const domainTotal = domainIndicators.length;
            totalIndicators += domainTotal;

            const domainResponses = (currentAudit.responses || []).filter(r =>
                domainIndicators.some(i => i.indicator_id === r.indicator_id)
            );

            const domainScore = domainResponses.reduce((sum, r) => sum + (r.selected_level || 0), 0);
            const avgLevel = domainTotal > 0 ? domainScore / domainTotal : 0;
            answeredCount += domainResponses.length;
            totalLevelSum += avgLevel;

            const weightage = domain.weightage || 10;
            totalWeightage += weightage;
            weightedScoreSum += (avgLevel / 4) * weightage;
        });

        const overallScore = totalWeightage > 0 ? (weightedScoreSum / totalWeightage) * 100 : 0;
        const avgLevel = framework.length > 0 ? totalLevelSum / framework.length : 0;

        return {
            percent: Math.round((answeredCount / totalIndicators) * 100) || 0,
            answered: answeredCount,
            total: totalIndicators,
            score: overallScore,
            avgLevel: avgLevel
        };
    };

    const getMaturityInfo = (level) => {
        if (level >= 3.5) return { label: 'Dynamic', color: 'text-green-600', bg: 'bg-green-100', border: 'border-green-500', desc: 'Excellence with continuous improvement' };
        if (level >= 2.5) return { label: 'Stable', color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-500', desc: 'Documented practices with regular implementation' };
        if (level >= 1.5) return { label: 'Transient', color: 'text-amber-600', bg: 'bg-amber-100', border: 'border-amber-500', desc: 'Developing practices, some gaps remain' };
        return { label: 'Inceptive', color: 'text-red-600', bg: 'bg-red-100', border: 'border-red-500', desc: 'Early stages, needs significant development' };
    };

    const getAuditStatus = () => {
        if (!currentAudit) return 'not_started';
        if (currentAudit.status === 'submitted') return 'submitted';
        const stats = getStats();
        if (stats.percent === 100) return 'ready_to_submit';
        if (stats.percent > 0) return 'in_progress';
        return 'not_started';
    };

    // Loading State
    if (loading) {
        return (
            <Wrap>
                <div className="flex flex-col items-center justify-center min-h-screen bg-white">
                    <Loader2 className="w-12 h-12 text-[#D67C2C] animate-spin mb-4" />
                    <p className="text-gray-500 font-[Poppins]">Loading SQAAF Framework...</p>
                </div>
            </Wrap>
        );
    }

    // View Modes
    if (viewMode === 'wizard') {
        return (
            <Wrap>
                <div className="bg-white min-h-screen font-[Poppins]">
                    <SQAAFQuizWizard
                        key={currentAudit?._id || 'new'}
                        domains={framework}
                        initialAudit={currentAudit}
                        onComplete={() => {
                            showNotification('success', 'Assessment completed!');
                            setViewMode('dashboard');
                            loadData();
                        }}
                        onExit={() => {
                            setViewMode('dashboard');
                            loadData();
                        }}
                    />
                </div>
            </Wrap>
        );
    }

    if (viewMode === 'report') {
        return (
            <Wrap>
                <div className="bg-white min-h-screen font-[Poppins]">
                    <SQAAFScoreReport
                        audit={currentAudit}
                        domains={framework}
                        onBack={() => setViewMode('dashboard')}
                        onSubmitSuccess={() => {
                            showNotification('success', 'Audit submitted successfully!');
                            loadData();
                        }}
                    />
                </div>
            </Wrap>
        );
    }

    if (viewMode === 'actionplan') {
        return (
            <Wrap>
                <div className="bg-white min-h-screen font-[Poppins]">
                    <SQAAFActionPlan audit={currentAudit} onBack={() => setViewMode('dashboard')} />
                </div>
            </Wrap>
        );
    }

    if (viewMode === 'history') {
        return (
            <Wrap>
                <div className="bg-white min-h-screen font-[Poppins]">
                    <SQAAFHistory onBack={() => setViewMode('dashboard')} />
                </div>
            </Wrap>
        );
    }

    if (viewMode === 'action_plan') {
        return (
            <Wrap>
                <div className="bg-white min-h-screen font-[Poppins]">
                    <SQAAFActionPlan
                        audit={currentAudit}
                        onBack={() => setViewMode('dashboard')}
                    />
                </div>
            </Wrap>
        );
    }

    // Dashboard View (Default)
    const stats = getStats();
    const maturity = getMaturityInfo(stats.avgLevel);
    const auditStatus = getAuditStatus();

    // Stepper steps
    const steps = [
        { id: 'assess', label: 'Take Assessment', icon: ClipboardCheck, complete: auditStatus !== 'not_started' },
        { id: 'review', label: 'Review Results', icon: BarChart2, complete: stats.percent === 100 },
        { id: 'plan', label: 'Get Action Plan', icon: Target, complete: auditStatus === 'submitted' }
    ];

    return (
        <Wrap>
            <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[Poppins]">
                <div className="w-full">

                    {/* Notification */}
                    {notification && (
                        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 ${notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                            }`}>
                            {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                            <span>{notification.message}</span>
                        </div>
                    )}

                    {/* Restart Confirmation Modal */}
                    {showRestartConfirm && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2 bg-amber-100 rounded-full">
                                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-800">Restart Assessment?</h3>
                                </div>
                                <p className="text-gray-600 mb-6">
                                    This will archive your current assessment and start fresh.
                                    Your previous responses will be saved in history.
                                </p>
                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={() => setShowRestartConfirm(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={restartAudit}
                                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                                    >
                                        Yes, Restart
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Header */}
                    <div className="mb-8">
                        {!noLayout && (
                            <button
                                onClick={() => navigate('/SQAAF')}
                                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#D67C2C] transition-colors mb-4"
                            >
                                <ChevronLeft className="w-4 h-4" />
                                All SQAAF Plans
                            </button>
                        )}
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#D67C2C] text-white text-sm font-medium mb-3">
                                    <Award className="w-4 h-4 mr-2" />
                                    SQAAF {resolvedYear || ''}
                                </div>
                                <h1 className="text-3xl font-bold text-[#1D3469]">
                                    School Quality Assessment
                                </h1>
                                <p className="text-gray-500 mt-1">
                                    Evaluate your school across 7 domains aligned with NEP/NCF guidelines
                                </p>
                            </div>

                            {/* Status Badge */}
                            <div className={`px-4 py-2 rounded-lg ${maturity.bg} ${maturity.border} border-2`}>
                                <p className="text-xs text-gray-600 mb-1">Current Level</p>
                                <p className={`font-bold ${maturity.color}`}>{maturity.label}</p>
                            </div>
                        </div>

                        {/* Journey Stepper */}
                        <div className="bg-gray-50 rounded-xl p-4">
                            <div className="flex items-center justify-between max-w-2xl mx-auto">
                                {steps.map((step, idx) => (
                                    <React.Fragment key={step.id}>
                                        <div className="flex flex-col items-center">
                                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${step.complete
                                                ? 'bg-green-500 text-white'
                                                : idx === 0 || steps[idx - 1].complete
                                                    ? 'bg-[#D67C2C] text-white'
                                                    : 'bg-gray-200 text-gray-400'
                                                }`}>
                                                {step.complete ? <CheckCircle className="w-6 h-6" /> : <step.icon className="w-5 h-5" />}
                                            </div>
                                            <span className={`text-xs mt-2 font-medium ${step.complete ? 'text-green-600' : 'text-gray-600'}`}>
                                                {step.label}
                                            </span>
                                        </div>
                                        {idx < steps.length - 1 && (
                                            <div className={`flex-1 h-1 mx-2 rounded ${step.complete ? 'bg-green-500' : 'bg-gray-200'
                                                }`} />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Main Content Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

                        {/* Progress Card — soft white with orange accent */}
                        <div className="bg-white rounded-2xl p-6 border-2 border-orange-100 shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D67C2C] to-[#e89d4f] rounded-t-2xl" />
                            <Activity className="absolute -bottom-4 -right-4 w-24 h-24 text-orange-50" />
                            <p className="text-gray-500 text-sm font-medium mb-2">Assessment Progress</p>
                            <div className="text-5xl font-bold text-[#D67C2C] mb-3">{stats.percent}%</div>
                            <div className="w-full bg-orange-100 rounded-full h-2 mb-3">
                                <div
                                    className="bg-gradient-to-r from-[#D67C2C] to-[#e89d4f] h-full rounded-full transition-all duration-500"
                                    style={{ width: `${stats.percent}%` }}
                                />
                            </div>
                            <div className="text-gray-400 text-sm">{stats.answered} of {stats.total} indicators completed</div>
                        </div>

                        {/* Score Card */}
                        <div
                            onClick={() => setViewMode('report')}
                            className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#D67C2C] cursor-pointer transition-all shadow-sm group"
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Target className="w-5 h-5 text-[#D67C2C]" />
                                    <p className="text-gray-500 text-sm font-medium">Overall Score</p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#D67C2C] transition-colors" />
                            </div>
                            <div className="text-4xl font-bold text-[#D67C2C] mb-3">
                                {stats.score > 0 ? stats.score.toFixed(1) + '%' : '0.0%'}
                            </div>
                            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${maturity.bg} ${maturity.color}`}>
                                {maturity.label} Level
                            </div>
                            <p className="text-gray-400 text-xs mt-2">{maturity.desc}</p>
                        </div>

                        {/* Action Card */}
                        <div className="bg-white rounded-2xl p-6 border-2 border-gray-100 shadow-sm">
                            <p className="text-gray-500 text-sm font-medium mb-4">Quick Actions</p>

                            {/* Primary CTA based on status */}
                            {auditStatus === 'not_started' && (
                                <button
                                    onClick={startAudit}
                                    className="w-full bg-[#D67C2C] hover:bg-[#c06a25] text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors mb-3"
                                >
                                    <Play className="w-5 h-5" />
                                    Start Assessment
                                </button>
                            )}

                            {auditStatus === 'in_progress' && (
                                <>
                                    <button
                                        onClick={() => setViewMode('wizard')}
                                        className="w-full bg-[#D67C2C] hover:bg-[#c06a25] text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors mb-3"
                                    >
                                        <ArrowRight className="w-5 h-5" />
                                        Continue Assessment
                                    </button>
                                    <button
                                        onClick={() => setShowRestartConfirm(true)}
                                        className="w-full border border-gray-200 text-gray-600 py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-gray-50"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                        Restart Assessment
                                    </button>
                                </>
                            )}

                            {auditStatus === 'ready_to_submit' && (
                                <>
                                    <button
                                        onClick={() => setViewMode('report')}
                                        className="w-full bg-green-600 hover:bg-green-700 text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors mb-3"
                                    >
                                        <CheckCircle className="w-5 h-5" />
                                        Review & Submit
                                    </button>
                                    <button
                                        onClick={() => setViewMode('wizard')}
                                        className="w-full border border-gray-200 text-gray-600 py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-gray-50 mb-2"
                                    >
                                        Edit Responses
                                    </button>
                                    <button
                                        onClick={() => setShowRestartConfirm(true)}
                                        className="w-full border border-gray-200 text-gray-500 py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-gray-50"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                        Retake Assessment (Fresh Start)
                                    </button>
                                </>
                            )}

                            {auditStatus === 'submitted' && (
                                <>
                                    <button
                                        onClick={() => setViewMode('action_plan')}
                                        className="w-full bg-[#D67C2C] hover:bg-[#c06a25] text-white py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors mb-3"
                                    >
                                        <Sparkles className="w-5 h-5" />
                                        Get AI Action Plan
                                    </button>
                                    <button
                                        onClick={() => setShowRestartConfirm(true)}
                                        className="w-full border border-gray-200 text-gray-600 py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-2 hover:bg-gray-50"
                                    >
                                        <RefreshCw className="w-4 h-4" />
                                        Take New Assessment
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Teacher Activity Stats */}
                    {teacherStats && (
                        <div className="bg-orange-50 rounded-xl p-4 mb-8 border border-orange-100">
                            <div className="flex items-center gap-6">
                                <div className="flex items-center gap-2">
                                    <Users className="w-5 h-5 text-[#D67C2C]" />
                                    <span className="text-sm font-medium text-gray-700">Teacher Activity:</span>
                                </div>
                                <div className="flex gap-6">
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl font-bold text-[#D67C2C]">{teacherStats.total_lesson_plans || 0}</span>
                                        <span className="text-sm text-gray-500">Lesson Plans</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl font-bold text-[#e89d4f]">{teacherStats.total_assessments || 0}</span>
                                        <span className="text-sm text-gray-500">Assessments</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 ml-auto text-xs text-[#D67C2C] bg-orange-100 px-2 py-1 rounded">
                                    <Info className="w-3 h-3" />
                                    This data informs your SQAAF responses
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Domain Breakdown — table-card layout */}
                    <div className="mb-8">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-[#1D3469] flex items-center gap-2">
                                <BarChart2 className="w-5 h-5 text-[#D67C2C]" />
                                Domain Breakdown
                            </h2>
                            <span className="text-xs text-gray-400 font-medium">{framework?.length || 0} domains</span>
                        </div>

                        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                            {/* Table header */}
                            <div className="grid grid-cols-12 gap-4 px-5 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                <div className="col-span-1">#</div>
                                <div className="col-span-4">Domain</div>
                                <div className="col-span-2 text-center">Indicators</div>
                                <div className="col-span-3">Progress</div>
                                <div className="col-span-1 text-center">Level</div>
                                <div className="col-span-1 text-center">Status</div>
                            </div>

                            {/* Table rows */}
                            {framework?.map((domain, idx) => {
                                const domainIndicators = (domain.subdomains || []).flatMap(s => s.indicators || []);
                                const domainTotal = domainIndicators.length;
                                const domainResponses = (currentAudit?.responses || []).filter(r =>
                                    domainIndicators.some(i => i.indicator_id === r.indicator_id)
                                );
                                const domainProgress = domainTotal > 0 ? Math.round((domainResponses.length / domainTotal) * 100) : 0;
                                const avgLevel = domainTotal > 0
                                    ? domainResponses.reduce((sum, r) => sum + (r.selected_level || 0), 0) / domainTotal
                                    : 0;
                                const levelInfo = getMaturityInfo(avgLevel);

                                const DOMAIN_ICON_COLORS = [
                                    { bg: 'bg-blue-100',   icon: 'text-blue-600'   },
                                    { bg: 'bg-orange-100', icon: 'text-orange-600' },
                                    { bg: 'bg-teal-100',   icon: 'text-teal-600'   },
                                    { bg: 'bg-violet-100', icon: 'text-violet-600' },
                                    { bg: 'bg-emerald-100',icon: 'text-emerald-600'},
                                    { bg: 'bg-rose-100',   icon: 'text-rose-600'   },
                                    { bg: 'bg-indigo-100', icon: 'text-indigo-600' },
                                ];
                                const ic = DOMAIN_ICON_COLORS[idx % DOMAIN_ICON_COLORS.length];

                                const getDomainIcon = (i) => {
                                    const icons = [GraduationCap, Building2, Users, HeartHandshake, Briefcase, Flag, Smile];
                                    return icons[i] || BookOpen;
                                };
                                const DomainIcon = getDomainIcon(idx);

                                const PROGRESS_COLORS = [
                                    'bg-[#D67C2C]', 'bg-[#D67C2C]', 'bg-[#D67C2C]',
                                    'bg-[#D67C2C]', 'bg-[#D67C2C]', 'bg-[#D67C2C]', 'bg-[#D67C2C]'
                                ];
                                const barColor = PROGRESS_COLORS[idx % PROGRESS_COLORS.length];

                                return (
                                    <div
                                        key={domain.domain_id}
                                        onClick={() => setViewMode('wizard')}
                                        className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer group items-center"
                                    >
                                        {/* # */}
                                        <div className="col-span-1">
                                            <div className={`w-8 h-8 rounded-lg ${ic.bg} flex items-center justify-center`}>
                                                <DomainIcon className={`w-4 h-4 ${ic.icon}`} />
                                            </div>
                                        </div>

                                        {/* Domain name */}
                                        <div className="col-span-4">
                                            <p className="text-sm font-semibold text-gray-800 group-hover:text-[#D67C2C] transition-colors leading-snug">
                                                {domain.domain_name}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                {domain.subdomains?.length || 0} sub-domains
                                            </p>
                                        </div>

                                        {/* Indicators count */}
                                        <div className="col-span-2 text-center">
                                            <span className="text-sm font-bold text-gray-700">{domainResponses.length}</span>
                                            <span className="text-xs text-gray-400"> / {domainTotal}</span>
                                        </div>

                                        {/* Progress bar */}
                                        <div className="col-span-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                                                        style={{ width: `${domainProgress}%` }}
                                                    />
                                                </div>
                                                <span className="text-xs font-semibold text-gray-500 w-8 text-right flex-shrink-0">
                                                    {domainProgress}%
                                                </span>
                                            </div>
                                        </div>

                                        {/* Avg level */}
                                        <div className="col-span-1 text-center">
                                            {avgLevel > 0 ? (
                                                <span className={`inline-block text-xs font-bold px-2 py-0.5 rounded-full ${levelInfo.bg} ${levelInfo.color}`}>
                                                    L{avgLevel.toFixed(1)}
                                                </span>
                                            ) : (
                                                <span className="text-xs text-gray-300">—</span>
                                            )}
                                        </div>

                                        {/* Status dot */}
                                        <div className="col-span-1 flex justify-center">
                                            {domainProgress === 100 ? (
                                                <CheckCircle className="w-4 h-4 text-green-500" />
                                            ) : domainProgress > 0 ? (
                                                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                            ) : (
                                                <div className="w-2.5 h-2.5 rounded-full bg-gray-200" />
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Quick Action Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <button
                            onClick={() => setViewMode('report')}
                            className="bg-white rounded-xl p-5 border border-gray-200 hover:border-[#D67C2C] hover:shadow-md transition-all text-left group shadow-sm"
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-orange-100 rounded-lg">
                                    <BarChart2 className="w-5 h-5 text-[#D67C2C]" />
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-300 ml-auto group-hover:text-[#D67C2C] transition-colors" />
                            </div>
                            <h4 className="font-bold text-gray-800 group-hover:text-[#D67C2C] transition-colors">View Detailed Report</h4>
                            <p className="text-sm text-gray-500 mt-1">See maturity profile and domain analysis</p>
                        </button>

                        <button
                            onClick={() => setViewMode('action_plan')}
                            className="bg-white rounded-xl p-5 border border-gray-200 hover:border-[#D67C2C] hover:shadow-md transition-all text-left group shadow-sm"
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-orange-100 rounded-lg">
                                    <Sparkles className="w-5 h-5 text-[#D67C2C]" />
                                </div>
                                <span className="text-xs font-semibold text-[#D67C2C] bg-orange-50 px-2 py-0.5 rounded-full ml-auto">AI-POWERED</span>
                            </div>
                            <h4 className="font-bold text-gray-800 group-hover:text-[#D67C2C] transition-colors">Get Action Plan</h4>
                            <p className="text-sm text-gray-500 mt-1">AI recommendations for each indicator</p>
                        </button>

                        <button
                            onClick={() => setViewMode('history')}
                            className="bg-white rounded-xl p-5 border border-gray-200 hover:border-[#D67C2C] hover:shadow-md transition-all text-left group shadow-sm"
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-2 bg-orange-100 rounded-lg">
                                    <History className="w-5 h-5 text-[#D67C2C]" />
                                </div>
                                <TrendingUp className="w-4 h-4 text-gray-300 ml-auto group-hover:text-[#D67C2C] transition-colors" />
                            </div>
                            <h4 className="font-bold text-gray-800 group-hover:text-[#D67C2C] transition-colors">Assessment History</h4>
                            <p className="text-sm text-gray-500 mt-1">Compare year-over-year performance</p>
                        </button>
                    </div>
                </div>
            </div>
        </Wrap>
    );
};

export default SQAAF;